import { FactiiiRequirement } from './factiiis';

// The Dark Patterns space and its factiiis (.specs/dark-patterns.md).
//
// Every string in this file is published by the platform: a factiii's
// description is its page's meta description and share text. So each one
// describes what a post shows and never says anything about a company, and
// darkPatterns.test.ts fails on a word that would read as a ruling.
//
// The legal status of each pattern type is deliberately not here. It stays in
// .todos/dark-patterns.md and is not seeded or rendered.

export const DARK_PATTERNS_SPACE_SLUG = 'dark-patterns';
export const DARK_PATTERNS_SPACE_NAME = 'Dark Patterns';
export const DARK_PATTERNS_SPACE_DESCRIPTION =
  'Documented examples of interface designs that steer people into choices they did not intend. Every entry is one user’s observation, with a screenshot or recording, the page where it was seen and the date.';

export type DarkPattern = {
  name: string;
  slug: string;
  /** What a post under this factiii shows, as something a viewer can see. */
  definition: string;
  /** A still cannot show something resetting, so these take a video only. */
  needsRecording: boolean;
};

export const DARK_PATTERNS: DarkPattern[] = [
  {
    name: 'Hard to Cancel',
    slug: 'hard-to-cancel',
    definition:
      'a sign-up that takes a few steps and a cancellation that takes many more, such as cancelling by phone only or through a long series of screens',
    needsRecording: false
  },
  {
    name: 'Forced Continuity',
    slug: 'forced-continuity',
    definition:
      'a free trial that becomes a paid subscription with no reminder before the first charge',
    needsRecording: false
  },
  {
    name: 'Sneak into Basket',
    slug: 'sneak-into-basket',
    definition:
      'add-ons such as insurance, a warranty or a donation that are already selected in the basket',
    needsRecording: false
  },
  {
    name: 'Hidden Costs',
    slug: 'hidden-costs',
    definition: 'fees that first appear at the final step of checkout',
    needsRecording: false
  },
  {
    name: 'Drip Pricing',
    slug: 'drip-pricing',
    definition:
      'mandatory fees added one step at a time, so the total rises as checkout goes on',
    needsRecording: false
  },
  {
    name: 'Confirmshaming',
    slug: 'confirmshaming',
    definition:
      'an opt-out worded so that declining reads as something to be ashamed of',
    needsRecording: false
  },
  {
    name: 'Misdirection',
    slug: 'misdirection',
    definition:
      'visual emphasis that draws attention to the option that costs or shares more',
    needsRecording: false
  },
  {
    name: 'Trick Question',
    slug: 'trick-question',
    definition:
      'opt-in wording that is hard to follow, such as a double negative',
    needsRecording: false
  },
  {
    name: 'Disguised Ad',
    slug: 'disguised-ad',
    definition:
      'advertising styled to look like content, navigation or a download button',
    needsRecording: false
  },
  {
    name: 'Fake Urgency',
    slug: 'fake-urgency',
    definition:
      'a countdown or low-stock notice that starts over when the page is reloaded',
    needsRecording: true
  },
  {
    name: 'Fake Social Proof',
    slug: 'fake-social-proof',
    definition:
      'purchase or visitor activity notices that repeat, or come back unchanged after a reload',
    needsRecording: true
  },
  {
    name: 'Nagging',
    slug: 'nagging',
    definition: 'a request that keeps interrupting until the person accepts it',
    needsRecording: false
  },
  {
    name: 'Bait and Switch',
    slug: 'bait-and-switch',
    definition: 'a control that does something other than what its label says',
    needsRecording: false
  },
  {
    name: 'Obstruction',
    slug: 'obstruction',
    definition:
      'extra steps placed in front of an action such as cancelling, unsubscribing or deleting an account',
    needsRecording: false
  },
  {
    name: 'Privacy Zuckering',
    slug: 'privacy-zuckering',
    definition:
      'settings or prompts that lead to sharing more personal data than intended, such as sharing that is on by default or switches placed several screens deep',
    needsRecording: false
  }
];

export const darkPatternDescription = (pattern: DarkPattern) =>
  `Posts showing: ${pattern.definition}. Each is one user’s observation with evidence attached.`;

// What a tag must carry (.specs/dark-patterns.md → Evidence). The company is a
// source factiii and the page is that reference's URL, both under
// SOURCE_REQUIRED; the bias statement is the tag's data.
export const darkPatternRequirements = (
  pattern: DarkPattern
): FactiiiRequirement[] => [
  pattern.needsRecording
    ? FactiiiRequirement.VIDEO_REQUIRED
    : FactiiiRequirement.MEDIA_REQUIRED,
  FactiiiRequirement.SOURCE_REQUIRED,
  FactiiiRequirement.TIME_REQUIRED,
  FactiiiRequirement.DATA_REQUIRED,
  FactiiiRequirement.POSTFACTIII_MODERATION_REQUIRED
];

/** The reply a company, or someone for it, posts to a post in the space. */
export const COMPANY_RESPONSE = {
  name: 'Company Response',
  slug: 'company-response',
  description:
    'A reply from, or on behalf of, a company to a post in this space. It cites the post it answers.',
  requirements: [
    FactiiiRequirement.POST_REFERENCE_REQUIRED,
    FactiiiRequirement.CONTENT_REQUIRED,
    FactiiiRequirement.POSTFACTIII_MODERATION_REQUIRED
  ]
};

// The two tag inputs that read differently for a pattern factiii.
export const DARK_PATTERN_DATA_PROMPT =
  'Which bias does this exploit, and who benefits?';
export const DARK_PATTERN_SOURCE_LINK_PROMPT =
  'Link to the exact page or screen';

/**
 * Whether a factiii is one of the 15 pattern factiiis. Matched on both slugs,
 * with no schema flag: a `nagging` factiii in another space is someone else's.
 */
export const isDarkPatternFactiii = (factiii: {
  slug: string;
  spaceSlug?: string | null;
}) =>
  factiii.spaceSlug === DARK_PATTERNS_SPACE_SLUG &&
  DARK_PATTERNS.some((pattern) => pattern.slug === factiii.slug);

/** Every string above that reaches a page, for the no-verdict test. */
export const darkPatternPlatformCopy = (): string[] => [
  DARK_PATTERNS_SPACE_NAME,
  DARK_PATTERNS_SPACE_DESCRIPTION,
  ...DARK_PATTERNS.flatMap((pattern) => [
    pattern.name,
    darkPatternDescription(pattern)
  ]),
  COMPANY_RESPONSE.name,
  COMPANY_RESPONSE.description,
  DARK_PATTERN_DATA_PROMPT,
  DARK_PATTERN_SOURCE_LINK_PROMPT
];
